import { ICalendarEvent } from "components";
import { DateTime } from "luxon";
import { pluralize } from "services/Utils";
import { IHomeCalendarData } from "../Home";

/**
 * Looks like:
 *     Fryer Controller Update - Stage 1
 *     From Thursday, February 3, 2022, 5:00 AM to Monday, February 28, 2022, 5:00 AM 
 *     Affects 10 devices, 2 locations
 */
export function getEventTitle(event: ICalendarEvent<IHomeCalendarData>, timezone: string): string {
    const update = event.data.update;
    let updateLabel = '';

    // Build label
    if (event.isGroupGap) {
        const prevStageNum = event.groupGapPrevEvent.data.stageIndex + 1;
        const nextStageNum = event.groupGapNextEvent.data.stageIndex + 1;
        updateLabel = `${update.name} - Gap between stages ${prevStageNum} and ${nextStageNum}`;
    } else {
        // Only show "Stage 1" if the update has more than 1 stage.
        if (update.rollout.stages.length > 1) {
            updateLabel = `${update.name} - Stage ${event.data.stageIndex + 1}`
        } else {
            updateLabel = update.name;
        }
    }

    // Build dates string.
    const dates = formatEventDatesTooltip(event.start, event.end, timezone);

    let title = `${updateLabel} \nFrom ${dates}`

    // Append affected devices and locations if it's a stage and not a gap.
    if (!event.isGroupGap) {
        const stageCounts = update.counts?.stages?.[event.data.stageIndex];
        title += ` \nAffects ${stageCounts?.total || 0} ${pluralize(stageCounts?.total, 'device', 'devices')}, `
            + `${stageCounts?.totalLocations || 0} ${pluralize(stageCounts?.totalLocations, 'location', 'locations')}`;
    }

    return title;
}

function formatEventDatesTooltip(start: Date, end: Date, timezone: string): string {
    // Looks like: "Monday, October 1, 2022 8:00 AM"
    let dateFormat: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    };

    const startDt = DateTime.fromJSDate(start, { zone: timezone });
    const endDt = DateTime.fromJSDate(end, { zone: timezone });
    // Don't include day of week and dates if both dates are on the same day.
    if (startDt.hasSame(endDt, 'day')) {
        dateFormat = {
            hour: 'numeric',
            minute: '2-digit',
        };
    }

    return startDt.toLocaleString(dateFormat) + ' to ' + endDt.toLocaleString(dateFormat);
}